import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
    CogIcon, 
    ClockIcon, 
    BoltIcon, 
    ChartBarIcon,
    AdjustmentsHorizontalIcon,
    BellIcon,
    PlayIcon,
    PauseIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

function SmartTrackingSettingsPanel() {
    const { token } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [stats, setStats] = useState(null);

    // Varsayılan ayarlar
    const defaultSettings = {
        isActive: true,
        smartMode: true,
        batchSize: 5,
        maxConcurrent: 10,
        intervals: {
            reference: {
                min: 15000,
                max: 300000,
                default: 30000,
                maxAttempts: 120
            },
            tracking: {
                min: 1800000,
                max: 14400000,
                default: 3600000,
                maxAttempts: 24
            },
            status: {
                min: 3600000,
                max: 86400000,
                default: 7200000,
                maxAttempts: 168
            }
        },
        optimization: {
            enabled: true,
            adaptiveIntervals: true,
            priorityMode: true,
            batchOptimization: true,
            timeBasedAdjustment: true
        },
        notifications: {
            enabled: true,
            realTime: true,
            email: false,
            push: true,
            levels: ['error', 'success', 'warning']
        }
    };

    useEffect(() => {
        loadSettings();
        loadStats();
        
        // Her 30 saniyede istatistikleri güncelle
        const interval = setInterval(loadStats, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadSettings = async () => {
        try {
            const response = await api.get('/api/smart-tracking/settings', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.success) {
                setSettings({ ...defaultSettings, ...response.settings });
            } else {
                setSettings(defaultSettings);
            }
        } catch (error) {
            console.error('Ayarlar yüklenirken hata:', error);
            setSettings(defaultSettings);
            toast.error('Ayarlar yüklenemedi, varsayılanlar kullanılıyor.');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await api.get('/api/smart-tracking/stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('İstatistikler yüklenirken hata:', error);
        }
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            const response = await api.put('/api/smart-tracking/settings', settings, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.success) {
                toast.success('✅ Ayarlar başarıyla kaydedildi ve servis yeniden başlatıldı!');
                loadStats(); // İstatistikleri yenile
            } else {
                toast.error('❌ Ayarlar kaydedilemedi: ' + response.error);
            }
        } catch (error) {
            console.error('Ayarlar kaydedilirken hata:', error);
            toast.error('❌ Ayarlar kaydedilemedi: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const updateSetting = (path, value) => {
        const newSettings = { ...settings };
        const keys = path.split('.');
        let current = newSettings;
        
        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
        }
        
        current[keys[keys.length - 1]] = value;
        setSettings(newSettings);
    };

    const formatTime = (ms) => {
        if (ms < 60000) return `${ms / 1000}s`;
        if (ms < 3600000) return `${ms / 60000}dk`;
        return `${ms / 3600000}sa`;
    };

    const resetToDefaults = () => {
        if (window.confirm('Tüm ayarları varsayılan değerlere sıfırlamak istediğinizden emin misiniz?')) {
            setSettings(defaultSettings);
            toast.info('🔄 Ayarlar varsayılan değerlere sıfırlandı. Kaydetmeyi unutmayın!');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green"></div>
                <span className="ml-2 text-gray-600">Ayarlar yükleniyor...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center">
                            <AdjustmentsHorizontalIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Akıllı Takip Ayarları</h2>
                            <p className="text-gray-600">Gelişmiş takip sistemi konfigürasyonu</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={resetToDefaults}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            <ArrowPathIcon className="w-4 h-4 mr-2 inline" />
                            Sıfırla
                        </button>
                        <button
                            onClick={saveSettings}
                            disabled={saving}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50"
                        >
                            {saving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                                    Kaydediliyor...
                                </>
                            ) : (
                                'Kaydet'
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* İstatistikler */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <ChartBarIcon className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Aktif Takip</p>
                                <p className="text-xl font-bold text-gray-900">{stats.active}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <BoltIcon className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Başarı Oranı</p>
                                <p className="text-xl font-bold text-gray-900">{stats.serviceStatus?.stats?.successRate?.toFixed(1) || 0}%</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <ClockIcon className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Toplam İşlenen</p>
                                <p className="text-xl font-bold text-gray-900">{stats.total}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <BellIcon className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Başarısız</p>
                                <p className="text-xl font-bold text-gray-900">{stats.failed}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Temel Ayarlar */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CogIcon className="w-5 h-5 mr-2 text-gray-600" />
                    Temel Ayarlar
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Servis Durumu
                        </label>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => updateSetting('isActive', !settings.isActive)}
                                className={`flex items-center px-4 py-2 rounded-lg transition-all ${
                                    settings.isActive 
                                        ? 'bg-green-100 text-green-800 border border-green-200' 
                                        : 'bg-red-100 text-red-800 border border-red-200'
                                }`}
                            >
                                {settings.isActive ? (
                                    <PlayIcon className="w-4 h-4 mr-2" />
                                ) : (
                                    <PauseIcon className="w-4 h-4 mr-2" />
                                )}
                                {settings.isActive ? 'Aktif' : 'Pasif'}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Akıllı Mod
                        </label>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                checked={settings.smartMode}
                                onChange={(e) => updateSetting('smartMode', e.target.checked)}
                                className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-600">
                                Dinamik aralıklar ve öncelik sistemi
                            </span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Batch Boyutu
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="20"
                            value={settings.batchSize}
                            onChange={(e) => updateSetting('batchSize', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">Aynı anda işlenecek takip sayısı</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Maksimum Eşzamanlı
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="50"
                            value={settings.maxConcurrent}
                            onChange={(e) => updateSetting('maxConcurrent', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">Maksimum eşzamanlı işlem sayısı</p>
                    </div>
                </div>
            </div>

            {/* Aralık Ayarları */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <ClockIcon className="w-5 h-5 mr-2 text-gray-600" />
                    Kontrol Aralıkları
                </h3>
                
                {Object.entries(settings.intervals).map(([phase, config]) => (
                    <div key={phase} className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3 capitalize">
                            {phase === 'reference' ? 'Referans Numarası' : 
                             phase === 'tracking' ? 'Takip Numarası' : 'Durum Takibi'}
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Minimum ({formatTime(config.min)})
                                </label>
                                <input
                                    type="range"
                                    min={phase === 'reference' ? 10000 : phase === 'tracking' ? 600000 : 1800000}
                                    max={config.max}
                                    step={phase === 'reference' ? 5000 : 300000}
                                    value={config.min}
                                    onChange={(e) => updateSetting(`intervals.${phase}.min`, parseInt(e.target.value))}
                                    className="w-full"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Varsayılan ({formatTime(config.default)})
                                </label>
                                <input
                                    type="range"
                                    min={config.min}
                                    max={config.max}
                                    step={phase === 'reference' ? 5000 : 300000}
                                    value={config.default}
                                    onChange={(e) => updateSetting(`intervals.${phase}.default`, parseInt(e.target.value))}
                                    className="w-full"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Maksimum ({formatTime(config.max)})
                                </label>
                                <input
                                    type="range"
                                    min={config.default}
                                    max={phase === 'reference' ? 600000 : phase === 'tracking' ? 28800000 : 172800000}
                                    step={phase === 'reference' ? 15000 : 900000}
                                    value={config.max}
                                    onChange={(e) => updateSetting(`intervals.${phase}.max`, parseInt(e.target.value))}
                                    className="w-full"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Max Deneme
                                </label>
                                <input
                                    type="number"
                                    min="5"
                                    max="1000"
                                    value={config.maxAttempts}
                                    onChange={(e) => updateSetting(`intervals.${phase}.maxAttempts`, parseInt(e.target.value))}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-brand-green"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Optimizasyon Ayarları */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BoltIcon className="w-5 h-5 mr-2 text-gray-600" />
                    Optimizasyon Ayarları
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(settings.optimization).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <p className="font-medium text-gray-900">
                                    {key === 'enabled' ? 'Optimizasyon Aktif' :
                                     key === 'adaptiveIntervals' ? 'Uyarlanabilir Aralıklar' :
                                     key === 'priorityMode' ? 'Öncelik Modu' :
                                     key === 'batchOptimization' ? 'Batch Optimizasyonu' :
                                     key === 'timeBasedAdjustment' ? 'Zaman Bazlı Ayarlama' : key}
                                </p>
                                <p className="text-sm text-gray-600">
                                    {key === 'enabled' ? 'Tüm optimizasyon özelliklerini etkinleştir' :
                                     key === 'adaptiveIntervals' ? 'Başarı oranına göre aralıkları ayarla' :
                                     key === 'priorityMode' ? 'Yeni siparişleri önceliklendir' :
                                     key === 'batchOptimization' ? 'Toplu işleme optimizasyonu' :
                                     key === 'timeBasedAdjustment' ? 'Gece/gündüz farklı hızlar' : ''}
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={value}
                                onChange={(e) => updateSetting(`optimization.${key}`, e.target.checked)}
                                className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Bildirim Ayarları */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BellIcon className="w-5 h-5 mr-2 text-gray-600" />
                    Bildirim Ayarları
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                            <p className="font-medium text-gray-900">Bildirimler Aktif</p>
                            <p className="text-sm text-gray-600">Tüm bildirimleri etkinleştir</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.notifications.enabled}
                            onChange={(e) => updateSetting('notifications.enabled', e.target.checked)}
                            className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                        />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                            <p className="font-medium text-gray-900">Real-time Bildirimler</p>
                            <p className="text-sm text-gray-600">Anlık durum güncellemeleri</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.notifications.realTime}
                            onChange={(e) => updateSetting('notifications.realTime', e.target.checked)}
                            className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                        />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                            <p className="font-medium text-gray-900">Email Bildirimleri</p>
                            <p className="text-sm text-gray-600">Önemli olaylar için email</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.notifications.email}
                            onChange={(e) => updateSetting('notifications.email', e.target.checked)}
                            className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                        />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                            <p className="font-medium text-gray-900">Push Bildirimleri</p>
                            <p className="text-sm text-gray-600">Tarayıcı bildirimleri</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.notifications.push}
                            onChange={(e) => updateSetting('notifications.push', e.target.checked)}
                            className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SmartTrackingSettingsPanel;